import { Heading, IllustratedMessage } from '@eleveo/components-react';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecordBooleanType } from '../../types';
import { IntradayChart, IntradayChartProps } from './IntradayChart';
import { IntradayChartInfoValue } from './IntradayChartInfoValue';
import { IntradayChartSeriesLegend } from './IntradayChartSeriesLegend';
import { chartsConfig } from './config';
import { IntradaySupportedSeries, Metadata, SeriesConfig } from './types';
import { determineSeriesEnabledState } from './utils';

export type IntradayChartsTileProps = Omit<IntradayChartProps, 'seriesDataVisibility' | 'seriesData'> & {
  isLoading: boolean;
  metadata: Metadata[];
};

export const prepareAllSeriesDataVisibleObject = (seriesNames: string[]) =>
  seriesNames.reduce((seriesVisibilityState, seriesName) => {
    if (seriesName === 'trend') {
      // do not store separate state for trend, this should be matching state of "seriesTrendLegendRelatedSeries"
      return seriesVisibilityState;
    }

    // eslint-disable-next-line no-param-reassign
    seriesVisibilityState[seriesName] = true;

    return seriesVisibilityState;
  }, {} as RecordBooleanType);

export const IntradayChartTile: React.FC<IntradayChartsTileProps> = ({ isLoading, metadata, ...chartProps }) => {
  const { t } = useTranslation();
  const { data, chartType } = chartProps;
  const {
    colorTag,
    id: chartId,
    nameKey,
    seriesConfig,
    seriesTrendLegendRelatedSeries,
    shouldRenderExtraSeriesLegendRow,
  } = chartsConfig[chartType];

  const seriesNames = useMemo(() => Object.keys(data ?? {}).filter((name) => name !== '__typename'), [data]);

  const seriesEnabledRecordObj = useMemo(
    () => determineSeriesEnabledState(seriesConfig as SeriesConfig<IntradaySupportedSeries>[], data),
    [seriesConfig, data]
  );

  const shouldRenderChart = useMemo(
    () => Object.values(seriesEnabledRecordObj).find((enabled) => enabled),
    [seriesEnabledRecordObj]
  );

  const [seriesDataVisibility, setSeriesDataVisibility] = useState(() =>
    prepareAllSeriesDataVisibleObject(seriesNames)
  );

  useEffect(() => {
    setSeriesDataVisibility(prepareAllSeriesDataVisibleObject(seriesNames));
  }, [seriesNames]);

  const toggleSeriesDataVisibility = (seriesName: string) => {
    const seriesNameToChange =
      seriesName === 'trend' && seriesTrendLegendRelatedSeries ? seriesTrendLegendRelatedSeries : seriesName;

    setSeriesDataVisibility((previousState) => ({
      ...previousState,
      [seriesNameToChange]: !seriesDataVisibility[seriesNameToChange],
    }));
  };

  const countOfVisibleSeries = useMemo(
    () => Object.values(seriesDataVisibility).filter((visible) => visible).length,
    [seriesDataVisibility]
  );

  return (
    <div
      className="flex flex-col border border-grey-300 bg-white h-full p-20 rounded"
      data-test-id={`intraday-management:${chartId}-chart:tile`}
    >
      <div className="intraday-chart-tile__header">
        <div className="flex flex-col items-start mie-auto">
          <Heading as="h2" className={clsx(`text-${colorTag}`)} data-test-id="chart:title">
            {t(`intraday.charts.${nameKey}`)}
          </Heading>
          {shouldRenderChart && (
            <IntradayChartSeriesLegend
              countOfVisibleSeries={countOfVisibleSeries}
              seriesConfig={seriesConfig as SeriesConfig<IntradaySupportedSeries>[]}
              seriesDataVisibility={seriesDataVisibility}
              seriesLegendEnabled={seriesEnabledRecordObj}
              seriesTrendLegendRelatedSeries={seriesTrendLegendRelatedSeries}
              shouldRenderExtraRow={shouldRenderExtraSeriesLegendRow}
              toggleSeriesDataVisibility={toggleSeriesDataVisibility}
            />
          )}
        </div>
        {!isLoading &&
          metadata.map((infoData) => (
            <div className="text-center" key={infoData.nameKey}>
              <p className="intraday-chart-tile__metadata-title mbe-15">
                {t(`intraday.charts.metadata.${infoData.nameKey}`)}
              </p>
              <p
                className={clsx('flex justify-center items-center', `text-${colorTag}`)}
                data-test-id={`chart:metadata-${infoData.id}`}
              >
                <IntradayChartInfoValue {...infoData} />
              </p>
            </div>
          ))}
      </div>
      {!isLoading && !shouldRenderChart && (
        <div className="flex flex-1 items-center justify-center" data-test-id="chart:no-data">
          <IllustratedMessage className="capitalize" heading={t('filter.noDataAvailable')} />
        </div>
      )}
      {!isLoading && shouldRenderChart && <IntradayChart {...chartProps} seriesDataVisibility={seriesDataVisibility} />}
    </div>
  );
};
